import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** An explanation that works with a pointer, keyboard, or a tap. */
export function HelpTip({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<CSSProperties>({});
  const show = () => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setOpen(false);
      return;
    }
    const width = Math.min(288, window.innerWidth - 24);
    setPosition({
      width,
      left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      ...(window.innerHeight - rect.bottom < 180
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (document.activeElement === trigger.current) show();
      else setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  return (
    <span className="help-tip" onMouseLeave={(event) => {
      if (document.activeElement === trigger.current) return;
      if (!(event.relatedTarget instanceof Node) || !tooltip.current?.contains(event.relatedTarget)) setOpen(false);
    }}>
      <button
        type="button"
        ref={trigger}
        className="help-tip__trigger"
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={show}
        onFocus={show}
        onBlur={() => setOpen(false)}
        onClick={show}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
          }
        }}
      >
        {label} <span aria-hidden="true">?</span>
      </button>
      {open ? createPortal(
        <span ref={tooltip} id={id} className="help-tip__body" style={position} role="tooltip" onMouseLeave={() => setOpen(false)}>{children}</span>,
        document.getElementById('main-content') ?? document.body,
      ) : null}
    </span>
  );
}
